<template>
  <div class="ctc-configuration">
    <v-card flat class="rmr-table-shell">
      <rmr-table-header controls-class="ctc-configuration__controls">
        <template #heading>
          <div class="rmr-table-shell__title">Sites RMR</div>
        </template>
        <template #controls>
          <div class="rmr-table-shell__filter-item rmr-table-shell__search">
            <label class="rmr-table-shell__filter-label">Recherche</label>
            <rmr-text-field
              v-model="search"
              :append-icon="icons.mdiMagnify"
              label="Rechercher un site"
              single-line
              clearable
            />
          </div>
          <div class="rmr-table-shell__filter-item ctc-configuration__filter">
            <label class="rmr-table-shell__filter-label">Disponibilité</label>
            <rmr-select
              v-model="readiness"
              :items="readinessItems"
              aria-label="Filtrer par disponibilité"
            />
          </div>
        </template>
      </rmr-table-header>

      <v-card-text class="rmr-table-shell__body">
        <v-data-table
          :headers="headers"
          :items="sites"
          :loading="loading"
          :options="options"
          @update:options="setOptions"
          :server-items-length="total"
          :footer-props="{ 'items-per-page-options': [25, 50, 100] }"
          loading-text="Chargement de la configuration CTC…"
          class="rmr-data-table"
        >
          <template v-slot:item.site="{ item }">
            <div class="ctc-configuration__site">
              <strong>{{ item.name }}</strong>
              <span>{{ item.domain }}</span>
            </div>
          </template>

          <template v-slot:item.affilizz="{ item }">
            <rmr-status-chip
              :label="item.hasAffilizzMedia ? 'Configuré' : 'Manquant'"
              :variant="item.hasAffilizzMedia ? 'success' : 'warning'"
              :tooltip="
                item.affilizzMediaName
                  ? `Média Affilizz : ${item.affilizzMediaName}`
                  : ''
              "
              size="sm"
            />
          </template>

          <template v-slot:item.cms="{ item }">
            <rmr-status-chip
              :label="item.hasCmsConnector ? 'Connecté' : 'Manquant'"
              :variant="item.hasCmsConnector ? 'success' : 'warning'"
              size="sm"
            />
          </template>

          <template v-slot:item.defaultSignature="{ item }">
            <span class="ctc-configuration__signature">
              {{ item.defaultSignature }}
            </span>
          </template>

          <template v-slot:item.enabled="{ item }">
            <rmr-status-chip
              :label="item.isEnabled ? 'Activé' : 'Désactivé'"
              :variant="item.isEnabled ? 'success' : 'neutral'"
              size="sm"
            />
          </template>

          <template v-slot:item.readiness="{ item }">
            <rmr-status-chip
              :label="item.isEligible ? 'Prêt' : 'Indisponible'"
              :variant="item.isEligible ? 'success' : 'warning'"
              :tooltip="
                item.blockingReasons.length
                  ? reasonLabels(item.blockingReasons).join(' · ')
                  : ''
              "
              size="sm"
            />
          </template>

          <template v-slot:item.actions="{ item }">
            <rmr-icon-button
              :icon="icons.mdiPencilOutline"
              variant="edit"
              tooltip="Configurer"
              :loading="savingIds.includes(item.id)"
              @click="selectedSite = item"
            />
          </template>

          <template v-slot:no-data>
            <div class="rmr-data-table__state">
              <div class="rmr-data-table__state-title">Aucun site</div>
              <div class="rmr-data-table__state-text">
                Aucun site ne correspond aux filtres sélectionnés.
              </div>
            </div>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <ctc-site-configuration-modal
      :value="!!selectedSite"
      :site="selectedSite"
      :saving="!!selectedSite && savingIds.includes(selectedSite.id)"
      @input="onModalVisibilityChange"
      @save="save"
    />
  </div>
</template>

<script>
import {
  computed,
  onBeforeUnmount,
  reactive,
  ref,
  watch,
} from "@vue/composition-api";
import { mdiMagnify, mdiPencilOutline } from "@mdi/js";

import RmrIconButton from "@/components/ui/actions/RmrIconButton.vue";
import RmrStatusChip from "@/components/ui/feedback/RmrStatusChip.vue";
import RmrSelect from "@/components/ui/forms/RmrSelect.vue";
import RmrTextField from "@/components/ui/forms/RmrTextField.vue";
import RmrTableHeader from "@/components/ui/layout/RmrTableHeader.vue";

import CtcSiteConfigurationModal from "./CtcSiteConfigurationModal.vue";

const ALL = "all";
const REASON_LABELS = {
  disabled: "Désactivé",
  affilizz_media_missing: "Pas de média Affilizz",
  cms_connector_missing: "Pas de connecteur CMS",
};

const HEADERS = [
  { text: "Site", value: "site", sortable: false },
  { text: "Affilizz", value: "affilizz", sortable: false },
  { text: "CMS", value: "cms", sortable: false },
  { text: "Signature par défaut", value: "defaultSignature", sortable: false },
  { text: "Actif", value: "enabled", sortable: false },
  { text: "État", value: "readiness", sortable: false },
  { text: "Actions", value: "actions", sortable: false, align: "end" },
];

export default {
  name: "CtcConfigurationView",
  components: {
    CtcSiteConfigurationModal,
    RmrIconButton,
    RmrSelect,
    RmrStatusChip,
    RmrTableHeader,
    RmrTextField,
  },
  props: {
    sites: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    total: {
      type: Number,
      default: 0,
    },
    page: {
      type: Number,
      default: 1,
    },
    limit: {
      type: Number,
      default: 50,
    },
    savingIds: {
      type: Array,
      default: () => [],
    },
  },
  setup(props, { emit }) {
    const search = ref("");
    const readiness = ref(ALL);
    const selectedSite = ref(null);
    const options = reactive({
      page: props.page,
      itemsPerPage: props.limit,
      sortBy: [],
      sortDesc: [],
    });
    // Not :options.sync: it would replace `options` on the instance while the
    // watchers below keep observing this object, so no page would ever load.
    const setOptions = (value) => Object.assign(options, value);
    let filterTimer = null;

    const load = () =>
      emit("load", {
        page: options.page,
        limit: options.itemsPerPage,
        search: (search.value || "").trim() || undefined,
        readiness: readiness.value === ALL ? undefined : readiness.value,
      });

    watch(
      () => [options.page, options.itemsPerPage],
      ([page, limit], [previousPage, previousLimit]) => {
        if (page === previousPage && limit === previousLimit) return;
        load();
      }
    );
    watch([search, readiness], () => {
      clearTimeout(filterTimer);
      filterTimer = setTimeout(() => {
        if (options.page !== 1) options.page = 1;
        else load();
      }, 300);
    });
    onBeforeUnmount(() => clearTimeout(filterTimer));

    return {
      setOptions,
      headers: HEADERS,
      icons: { mdiMagnify, mdiPencilOutline },
      onModalVisibilityChange: (visible) => {
        if (!visible) selectedSite.value = null;
      },
      options,
      readiness,
      readinessItems: [
        { text: "Tous les sites", value: ALL },
        { text: "Prêts", value: "ready" },
        { text: "Indisponibles", value: "blocked" },
      ],
      reasonLabels: (reasons) =>
        reasons.map((reason) => REASON_LABELS[reason] || reason),
      save: (payload, done) => {
        emit("save", payload, (success) => {
          if (success) {
            selectedSite.value = null;
            load();
          }
          if (typeof done === "function") done(success);
        });
      },
      search,
      selectedSite,
      total: computed(() => props.total),
    };
  },
};
</script>

<style lang="scss" scoped>
.ctc-configuration {
  display: grid;
  gap: 20px;
}

.ctc-configuration ::v-deep .ctc-configuration__controls {
  flex-wrap: wrap;
}

.ctc-configuration__filter {
  min-width: 200px;
}

.ctc-configuration__site,
.ctc-configuration__site {
  min-width: 170px;
  padding: 8px 0;
}

.ctc-configuration__site strong,
.ctc-configuration__signature {
  color: $rmr-text-primary;
}

.ctc-configuration__site span,
.ctc-configuration__signature {
  display: block;
  min-width: 210px;
  max-width: 300px;
  line-height: 1.4;
}
</style>
